import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, fmtDuration, fmtTime, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Tag as TagIcon, ChevronDown, X, Pencil, Search, ChevronLeft, ChevronRight, List, CalendarDays, Clock, Play, Square, AlignLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useTags } from "../context/TagsContext";


// ===== ADDED: Storage key for timer backup =====
const TIMER_STORAGE_KEY = "esstrack_active_timer";

// ===== 24-HOUR TIME INPUT COMPONENT =====
// ===== 24-HOUR TIME PICKER COMPONENT =====
function TimePicker24h({ value, onChange, className = "", testid = "" }) {
  const [open, setOpen] = useState(false);
  const [hour, setHour] = useState(() => (value ? value.split(":")[0] : "09"));
  const [minute, setMinute] = useState(() => (value ? value.split(":")[1] : "00"));
  const ref = useRef(null);
  const minuteColRef = useRef(null);

  useClickOutside(ref, () => setOpen(false), open);

  // Sync internal state when external value changes (e.g., editing existing entry)
  useEffect(() => {
    if (value) {
      setHour(value.split(":")[0]);
      setMinute(value.split(":")[1]);
    }
  }, [value, open]);

  const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
  const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

  const onSelectHour = (h) => {
    setHour(h);
    // Auto-scroll the minute column into a nice position
    setTimeout(() => {
      if (minuteColRef.current) {
        const activeMin = minuteColRef.current.querySelector("[data-active='true']");
        if (activeMin) {
          activeMin.scrollIntoView({ block: "center", behavior: "smooth" });
        }
      }
    }, 10);
  };

  const onSelectMinute = (m) => {
    setMinute(m);
    onChange(`${hour}:${m}`);
    setOpen(false);
  };

  // Scroll to active values when dropdown opens
  useEffect(() => {
    if (!open) return;
    setTimeout(() => {
      const container = ref.current;
      if (!container) return;
      const activeHour = container.querySelector(".hour-col [data-active='true']");
      const activeMin = container.querySelector(".min-col [data-active='true']");
      if (activeHour) activeHour.scrollIntoView({ block: "center" });
      if (activeMin) activeMin.scrollIntoView({ block: "center" });
    }, 20);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full border border-gray-200 bg-white px-3 py-3 text-sm text-left outline-none focus:border-[var(--ess-ink)] tabular-nums flex items-center justify-between ${className}`}
        data-testid={testid}
      >
        <span className={value ? "text-[var(--ess-ink)]" : "text-gray-400"}>
          {value || "HH:MM"}
        </span>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className={`text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}>
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div
          className="absolute top-full left-0 mt-1 bg-white border border-gray-200 z-30 shadow-lg flex rounded-sm overflow-hidden"
          style={{ height: "240px" }}
        >
          {/* Hours Column */}
          <div
            className="hour-col overflow-y-auto border-r border-gray-200 scrollbar-thin"
            style={{ width: "64px", height: "240px" }}
          >
            {hours.map((h) => (
              <button
                key={h}
                type="button"
                data-active={h === hour}
                onClick={() => onSelectHour(h)}
                className={`w-full px-2 py-1.5 text-sm text-center tabular-nums transition-colors ${
                  h === hour
                    ? "bg-[var(--ess-ink)] text-white font-semibold"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                {h}
              </button>
            ))}
          </div>

          {/* Minutes Column */}
          <div
            ref={minuteColRef}
            className="min-col overflow-y-auto scrollbar-thin"
            style={{ width: "64px", height: "240px" }}
          >
            {minutes.map((m) => (
              <button
                key={m}
                type="button"
                data-active={m === minute}
                onClick={() => onSelectMinute(m)}
                className={`w-full px-2 py-1.5 text-sm text-center tabular-nums transition-colors ${
                  m === minute
                    ? "bg-[var(--ess-ink)] text-white font-semibold"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
function nowHM() {
  const d = new Date();
  return d.toTimeString().slice(0, 5);
}

// ✅ ADD THIS: Get Indian Financial Year bounds
function getFYBounds() {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth() + 1; // 1-12
  
  let fyStart, fyEnd;
  
  if (month >= 4) {
    // April or later: FY is current year April to next year March
    fyStart = `${year}-04-01`;
    fyEnd = `${year + 1}-03-31`;
  } else {
    // Before April: FY is previous year April to current year March
    fyStart = `${year - 1}-04-01`;
    fyEnd = `${year}-03-31`;
  }
  
  return { start: fyStart, end: fyEnd };
}

function combineDateTime(dateStr, hm) {
  // returns ISO with local tz
  const [h, m] = hm.split(":").map(Number);
  const d = new Date(dateStr + "T00:00:00");
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}

// Close dropdown on outside click
function useClickOutside(ref, onOutside, active) {
  useEffect(() => {
    if (!active) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onOutside();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [ref, onOutside, active]);
}

export default function Dashboard() {
  const { user } = useAuth();
  const { tags: PREDEFINED_TAGS } = useTags(); // Get dynamic tags
  const [projects, setProjects] = useState([]);
  const [entries, setEntries] = useState([]);
  const [desc, setDesc] = useState("");
  const [projectId, setProjectId] = useState("");
  const [projectSearch, setProjectSearch] = useState("");
  const [selTags, setSelTags] = useState([]);
  const [tagSearch, setTagSearch] = useState(""); // ✅ ADDED: Tag search state
  const [tagsOpen, setTagsOpen] = useState(false);
  const [projectOpen, setProjectOpen] = useState(false);
  const [date, setDate] = useState(() => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
});
  const [start, setStart] = useState(nowHM());
  const [end, setEnd] = useState(nowHM());
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewMode, setViewMode] = useState("list"); // list | calendar
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [selectedDay, setSelectedDay] = useState(null); // ISO date string

  const tagsRef = useRef(null);
  const projectRef = useRef(null);
  useClickOutside(tagsRef, () => { setTagsOpen(false); setTagSearch(""); }, tagsOpen);
  useClickOutside(projectRef, () => { setProjectOpen(false); setProjectSearch(""); }, projectOpen);

  // Timer state
  const [entryMode, setEntryMode] = useState("manual"); // manual | timer
  const [timerStart, setTimerStart] = useState(null);   // ISO when running
  const [timerNow, setTimerNow] = useState(Date.now());
  const [pendingTimer, setPendingTimer] = useState(null); // { startISO, endISO } awaiting save
  const [activeTimerId, setActiveTimerId] = useState(null);
  const timerStartedThisSession = useRef(false);
  
  useEffect(() => {
    if (!timerStart) return;
    const id = setInterval(() => setTimerNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [timerStart]);
  const timerSecs = timerStart ? Math.floor((timerNow - new Date(timerStart).getTime()) / 1000) : 0;
  
    const loadAll = async () => {
    try {
      const localDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const today = localDateStr(new Date());
      
      let startDate;
      if (user.role === "admin") {
        // Admin: Load 7 days
        startDate = localDateStr(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
      } else {
        // Non-admin: Load current Financial Year
        const fyBounds = getFYBounds();
        startDate = fyBounds.start;
      }

      const [pRes, eRes] = await Promise.all([
        api.get("/projects"),
        api.get("/time-entries", { 
          params: { 
            start_date: startDate, 
            end_date: today 
          } 
        })
      ]);
      setProjects(pRes.data || []);
      setEntries(eRes.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { loadAll(); }, []);
  // ===== ADDED: Check for active timer on mount (backend + localStorage fallback) =====
    useEffect(() => {
    const checkActiveTimer = async () => {
      try {
        const res = await api.get("/timer/active");
        if (res.data.active && res.data.start_time) {
          const savedTimer = localStorage.getItem(TIMER_STORAGE_KEY);
          const isSameSession = savedTimer === res.data.start_time;
          
          setTimerStart(res.data.start_time);
          setActiveTimerId(res.data.id); // NEW: Store the DB row ID
          setEntryMode("timer");
          if (!isSameSession) {
          // NEW: Restore the saved form fields
            if (res.data.description) setDesc(res.data.description);
            if (res.data.project_id) setProjectId(res.data.project_id);
            if (res.data.tags && res.data.tags.length > 0) setSelTags(res.data.tags);
          }
          
          localStorage.setItem(TIMER_STORAGE_KEY, res.data.start_time);
          if (!isSameSession) {
            toast.success("Timer resumed from another device");
          }
        } else {
          localStorage.removeItem(TIMER_STORAGE_KEY);
          timerStartedThisSession.current = false;
        }
      } catch (e) {
        console.error("Failed to check for active timer", e);
        try {
          const savedTimer = localStorage.getItem(TIMER_STORAGE_KEY);
          if (savedTimer) {
            setTimerStart(savedTimer);
            setEntryMode("timer");
          }
        } catch (localStorageErr) {
          console.error("Could not read localStorage", localStorageErr);
        }
      }
    };
    checkActiveTimer();
  }, []);
  
  const duration = useMemo(() => {
    try {
      const s = new Date(combineDateTime(date, start));
      const e = new Date(combineDateTime(date, end));
      return Math.max(0, Math.floor((e - s) / 1000));
    } catch { return 0; }
  }, [date, start, end]);

  const onAdd = async () => {
    if (!desc.trim()) return toast.error("Description is required");
    if (!projectId) return toast.error("Project is required");
    if (selTags.length === 0) return toast.error("At least one tag is required");
    if (duration <= 0) return toast.error("End time must be after start time");

    setSubmitting(true);
    try {
      await api.post("/time-entries", {
        description: desc.trim(),
        project_id: projectId,
        tags: selTags,
        start_time: combineDateTime(date, start),
        end_time: combineDateTime(date, end),
      });
      toast.success("Logged");
      setDesc(""); setSelTags([]); setProjectId("");
      await loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const onStartTimer = async () => {
    const startTimeISO = new Date().toISOString();
    setTimerStart(startTimeISO);
    setTimerNow(Date.now());
    localStorage.setItem(TIMER_STORAGE_KEY, startTimeISO);
    timerStartedThisSession.current = true;
    
    try {
      const res = await api.post("/timer/start", {
        start_time: startTimeISO,
        description: desc.trim(),
        project_id: projectId || null,
        tags: selTags
      });
      setActiveTimerId(res.data.id); // NEW: Save ID returned from backend
    } catch (e) {
      console.error("Failed to sync timer start to backend", e);
    }
    
    toast.success("Timer started");
  };


  const onStopTimer = () => {
    const startISO = timerStart;
    const endISO = new Date().toISOString();
    // Immediately stop the UI timer
    setTimerStart(null);
    setActiveTimerId(null);
    localStorage.removeItem(TIMER_STORAGE_KEY);
    timerStartedThisSession.current = false;
    
    
      
     

     setPendingTimer({ 
      startISO, 
      endISO, 
      timerId: activeTimerId,
      description: desc.trim(), 
      projectId: projectId, 
      tags: selTags 
    });
    
    // 4. Clear the main form immediately
    setDesc("");
    setProjectId("");
    setSelTags([]);

    // 5. Tell backend to stop in the background (Fire-and-forget).
    // We pass endISO so even if server wakes up at 9:48 AM, it uses 9:45 AM!
    api.delete("/timer/stop", { data: { end_time: endISO } })
      .then((res) => {
        // Optional: If the backend successfully stopped it, we could update the pendingTimer ID 
        // if it was missing for some reason, but usually activeTimerId is sufficient.
        if (res.data && res.data.id) {
             setPendingTimer(prev => ({...prev, timerId: res.data.id}));
        }
      })
      .catch((e) => {
        // FAIL SILENTLY. The modal is already open. 
        // When the user clicks "Save" in the modal, it will retry the connection.
        console.log("Timer stop sent in background. Server might be sleeping, will finalize on Save.");
      });
  };
  const updateTimerEntry = async ({ timerId, startISO, endISO, description, projectId, tags }) => {
    setSubmitting(true);
    try {
      // CRITICAL FIX: Explicitly send start and end times so the backend 
      // calculates duration correctly, even if end_time was null in the DB.
      await api.put(`/time-entries/${timerId}`, {
        description, 
        project_id: projectId, 
        tags,
        start_time: startISO,
        end_time: endISO
      });
      toast.success("Logged");
      setDesc(""); setSelTags([]); setProjectId("");
      setPendingTimer(null);
      await loadAll();
    } catch (e) { 
      toast.error(formatApiError(e)); 
    }
    finally { 
      setSubmitting(false); 
    }
  };
  
  const onDelete = async (id) => {
    if (!window.confirm("Delete this log entry?")) return;
    try {
      await api.delete(`/time-entries/${id}`);
      setEntries((s) => s.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const e of entries) {
      // IGNORE e.date from backend - calculate from start_time in user's local timezone
      const localDate = getLocalDateStr(e.start_time);
      g[localDate] = g[localDate] || [];
      g[localDate].push(e);
      //g[e.date] = g[e.date] || [];
      //g[e.date].push(e);
    }
    return Object.entries(g).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [entries]);

  const displayTotal = useMemo(() => {
  if (user.role === "admin") {
    // Admin: Show current week total
    const today = new Date();
    const day = today.getDay();
    const monStart = new Date(today);
    monStart.setDate(today.getDate() - ((day + 6) % 7));
    const localDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const iso = localDateStr(monStart);
    return entries.filter((e) => e.date >= iso).reduce((s, e) => s + e.duration_seconds, 0);
  } else {
    // Non-admin: Show current Financial Year total
    // All loaded entries are already from FY start, so just sum them all
    return entries.reduce((s, e) => s + e.duration_seconds, 0);
  }
}, [entries, user.role]);

  const selectedProject = projects.find((p) => p.id === projectId);

  const filteredProjects = useMemo(() => {
    const q = projectSearch.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) =>
      p.name.toLowerCase().includes(q) || (p.client || "").toLowerCase().includes(q)
    );
  }, [projects, projectSearch]);

   // ✅ ADDED: Filter tags based on search
  const filteredTags = useMemo(() => {
    const q = tagSearch.trim().toLowerCase();
    if (!q) return PREDEFINED_TAGS;
    return PREDEFINED_TAGS.filter(t => t.toLowerCase().includes(q));
  }, [PREDEFINED_TAGS, tagSearch]);
  return (
    <div data-testid="dashboard-page">
      <div className="min-h-20 border-b border-gray-200 bg-white sticky top-0 z-20 flex items-center pl-16 lg:pl-8 pr-4 sm:pr-8 py-3 justify-between gap-3">
        <div>
          <div className="ess-section-label">Track</div>
          <div className="text-xl font-semibold tracking-tight text-[var(--ess-ink)]">Tracker</div>
        </div>
          <div className="text-right">
          <div className="ess-section-label">{user.role === "admin" ? "Week total" : "FY total"}</div>
          <div className="text-2xl font-medium tabular-nums text-[var(--ess-ink)]" data-testid="week-total">{fmtDuration(displayTotal)}</div>
        </div>
      </div>

      {/* Entry bar */}
      <div className="border-b border-gray-200 bg-[var(--ess-surface)] px-4 sm:px-8 py-5">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-3">
          <input
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="Task description..."
            className="lg:col-span-4 border border-gray-200 bg-white px-4 py-3 text-sm outline-none focus:border-[var(--ess-ink)]"
            data-testid="entry-description"
          />

          {/* Project */}
          <div className="lg:col-span-2 relative" ref={projectRef}>
            <button
              type="button"
              onClick={() => setProjectOpen((o) => !o)}
              className="w-full border border-gray-200 bg-white px-4 py-3 text-sm flex items-center justify-between hover:border-[var(--ess-ink)]"
              data-testid="entry-project-trigger"
            >
              <span className="truncate text-left">
                {selectedProject ? selectedProject.name : <span className="text-gray-400">+ Project</span>}
              </span>
              <ChevronDown size={14} />
            </button>
            {projectOpen && (
              <div className="absolute top-full left-0 mt-1 w-80 bg-white border border-gray-200 z-30 shadow-md">
                <div className="p-2 border-b border-gray-100 flex items-center gap-2">
                  <Search size={14} className="text-gray-400 flex-shrink-0" />
                  <input
                    autoFocus
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e.target.value)}
                    placeholder="Search projects..."
                    className="w-full text-sm outline-none"
                    data-testid="project-search-input"
                  />
                  {projectSearch && (
                    <button onClick={() => setProjectSearch("")} className="text-gray-400 hover:text-gray-600">
                      <X size={12} />
                    </button>
                  )}
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {filteredProjects.length === 0 && (
                    <div className="px-4 py-3 text-sm text-gray-400">No projects match.</div>
                  )}
                  {filteredProjects.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => { setProjectId(p.id); setProjectOpen(false); setProjectSearch(""); }}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 border-b border-gray-100 flex items-center justify-between ${projectId === p.id ? "bg-gray-50" : ""}`}
                      data-testid={`project-option-${p.id}`}
                    >
                      <span className="truncate">{p.name}</span>
                      <span className="text-xs text-gray-500 ml-2 flex-shrink-0">{p.client}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Tags */}
          <div className="lg:col-span-2 relative" ref={tagsRef}>
            <button
              type="button"
              onClick={() => setTagsOpen((o) => !o)}
              className="w-full border border-gray-200 bg-white px-4 py-3 text-sm flex items-center justify-between hover:border-[var(--ess-ink)]"
              data-testid="entry-tags-trigger"
            >
              <span className="truncate text-left flex items-center gap-1.5">
                <TagIcon size={14} />
                {selTags.length === 0 ? <span className="text-gray-400">Tags</span> : `${selTags.length} selected`}
              </span>
              <ChevronDown size={14} />
            </button>
            {tagsOpen && (
              <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-200 z-30 shadow-md flex flex-col max-h-80">
                <div className="p-2 border-b border-gray-100 flex items-center gap-2 bg-gray-50">
                  <Search size={14} className="text-gray-400 flex-shrink-0" />
                  <input
                    autoFocus
                    value={tagSearch}
                    onChange={(e) => setTagSearch(e.target.value)}
                    placeholder="Search tags..."
                    className="w-full text-sm outline-none bg-transparent"
                    data-testid="tag-search-input"
                    onClick={(e) => e.stopPropagation()} 
                  />
                  {tagSearch && (
                    <button onClick={() => setTagSearch("")} className="text-gray-400 hover:text-gray-600">
                      <X size={12} />
                    </button>
                  )}
                </div>
                <div className="overflow-y-auto flex-1">
                  {filteredTags.length === 0 && (
                    <div className="px-4 py-3 text-sm text-gray-400">No tags match.</div>
                  )}
                  {filteredTags.map((t) => (
                    <label key={t} className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0">
                      <input
                        type="checkbox"
                        checked={selTags.includes(t)}
                        onChange={(e) =>
                          setSelTags((s) => e.target.checked ? [...s, t] : s.filter((x) => x !== t))
                        }
                        data-testid={`tag-checkbox-${t}`}
                      />
                      {t}
                    </label>
                  ))}
                </div>
                <div className="p-2 border-t border-gray-200 flex justify-end bg-white sticky bottom-0">
                  <button onClick={() => setTagsOpen(false)} className="text-xs uppercase tracking-wider text-gray-600">Done</button>
                </div>
              </div>
            )}
          </div>

          {entryMode === "manual" && (
            <>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="lg:col-span-1 border border-gray-200 bg-white px-2 py-3 text-sm outline-none focus:border-[var(--ess-ink)]"
                data-testid="entry-date"
              />
              <TimePicker24h
                value={start}
                onChange={setStart}
                className="lg:col-span-1"
                testid="entry-start"
              />
              <TimePicker24h
                value={end}
                onChange={setEnd}
                className="lg:col-span-1"
                testid="entry-end"
              />

              <button
                onClick={onAdd}
                disabled={submitting}
                className="lg:col-span-1 bg-[var(--ess-ink)] text-white px-4 py-3 text-sm font-semibold tracking-wider uppercase hover:bg-[var(--ess-ink-soft)] disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="entry-add-btn"
              >
                <Plus size={14} /> Add
              </button>
            </>
          )}

          {entryMode === "timer" && (
            <>
              <div className="lg:col-span-2 border border-gray-200 bg-white px-4 py-3 text-sm flex items-center justify-center tabular-nums font-medium text-[var(--ess-ink)]" data-testid="timer-display">
                {fmtDuration(timerSecs)}
              </div>
              {timerStart ? (
                <button
                  onClick={onStopTimer}
                  className="lg:col-span-1 bg-red-600 text-white px-4 py-3 text-sm font-semibold tracking-wider uppercase hover:bg-red-700 flex items-center justify-center gap-2"
                  data-testid="timer-stop-btn"
                >
                  <Square size={14} /> Stop
                </button>
              ) : (
                <button
                  onClick={onStartTimer}
                  className="lg:col-span-1 bg-[var(--ess-ink)] text-white px-4 py-3 text-sm font-semibold tracking-wider uppercase hover:bg-[var(--ess-ink-soft)] flex items-center justify-center gap-2"
                  data-testid="timer-start-btn"
                >
                  <Play size={14} /> Start
                </button>
              )}
            </>
          )}

          {/* Mode toggle (icon stack) */}
          <div className="lg:col-span-1 flex flex-col border border-gray-200 bg-white">
            <button
              onClick={() => !timerStart && setEntryMode("timer")}
              disabled={!!timerStart && entryMode !== "timer"}
              className={`flex-1 flex items-center justify-center py-1.5 ${entryMode === "timer" ? "bg-[var(--ess-ink)] text-white" : "text-gray-500 hover:text-[var(--ess-ink)]"}`}
              title="Timer mode"
              data-testid="mode-timer-btn"
            >
              <Clock size={14} />
            </button>
            <div className="h-px bg-gray-200" />
            <button
              onClick={() => !timerStart && setEntryMode("manual")}
              disabled={!!timerStart}
              className={`flex-1 flex items-center justify-center py-1.5 ${entryMode === "manual" ? "bg-[var(--ess-ink)] text-white" : "text-gray-500 hover:text-[var(--ess-ink)]"}`}
              title="Manual mode"
              data-testid="mode-manual-btn"
            >
              <AlignLeft size={14} />
            </button>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
          <span>Duration: <span className="tabular-nums text-[var(--ess-ink)]" data-testid="entry-duration">{fmtDuration(duration)}</span></span>
          {selTags.length > 0 && (
            <div className="flex gap-1.5 flex-wrap">
              {selTags.map((t) => (
                <span key={t} className="bg-white border border-gray-200 px-2 py-0.5 inline-flex items-center gap-1">
                  {t}
                  <button onClick={() => setSelTags((s) => s.filter((x) => x !== t))}><X size={10} /></button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Entries list / Calendar */}
      <div className="px-4 sm:px-8 pt-4 pb-2 flex items-center gap-2">
        <button
          onClick={() => setViewMode("list")}
          className={`px-3 py-1.5 text-xs uppercase tracking-wider flex items-center gap-1.5 ${viewMode === "list" ? "bg-[var(--ess-ink)] text-white" : "border border-gray-200"}`}
          data-testid="view-list"
        >
          <List size={13} /> List
        </button>
        <button
          onClick={() => setViewMode("calendar")}
          className={`px-3 py-1.5 text-xs uppercase tracking-wider flex items-center gap-1.5 ${viewMode === "calendar" ? "bg-[var(--ess-ink)] text-white" : "border border-gray-200"}`}
          data-testid="view-calendar"
        >
          <CalendarDays size={13} /> Calendar
        </button>
      </div>

      {viewMode === "calendar" && (
        <CalendarView
          entries={entries}
          month={calendarMonth}
          setMonth={setCalendarMonth}
          selectedDay={selectedDay}
          setSelectedDay={setSelectedDay}
          user={user}
          onEdit={setEditing}
          onDelete={onDelete}
        />
      )}

      {viewMode === "list" && (
      <div className="px-4 sm:px-8 py-6">
        {grouped.length === 0 && (
          <div className="text-center py-16 text-gray-500" data-testid="entries-empty">
            <div className="ess-section-label mb-2">No entries yet</div>
            <div className="text-sm">Start logging your work above.</div>
          </div>
        )}
        {grouped.map(([day, list]) => {
          const total = list.reduce((s, e) => s + e.duration_seconds, 0);
          //const d = new Date(day);
          const d = new Date(day + "T12:00:00");
          const label = d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
          return (
            <div key={day} className="mb-8" data-testid={`day-group-${day}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="ess-section-label">{label}</div>
                <div className="text-sm tabular-nums text-gray-500">Total: <span className="text-[var(--ess-ink)] font-medium">{fmtDuration(total)}</span></div>
              </div>
              <div className="border border-gray-200">
                {list.map((e) => (
                  <div key={e.id} className="flex flex-col md:grid md:grid-cols-12 md:items-center px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 gap-1 md:gap-0" data-testid={`entry-${e.id}`}>
                    <div className="md:col-span-4 text-sm text-[var(--ess-ink)] font-medium md:truncate flex flex-wrap items-center gap-2">
                      <span className="break-words">{e.description}</span>
                      {user.role === "admin" && e.user_id !== user.id && (
                        <span className="text-[10px] uppercase tracking-wider bg-gray-100 text-gray-700 px-1.5 py-0.5" data-testid={`entry-user-${e.id}`}>
                          {e.user_name}
                        </span>
                      )}
                    </div>
                    <div className="md:col-span-2 text-xs text-gray-500 truncate">{e.project_name}</div>
                    <div className="md:col-span-2 flex flex-wrap gap-1">
                      {e.tags.map((t) => (
                        <span key={t} className="text-[10px] uppercase tracking-wider bg-gray-100 px-1.5 py-0.5">{t}</span>
                      ))}
                    </div>
                    <div className="md:col-span-2 text-xs text-gray-500 tabular-nums">
                      {fmtTime(e.start_time)} – {fmtTime(e.end_time)}
                    </div>
                    <div className="flex items-center justify-between md:justify-end gap-3 md:col-span-2">
                      <div className="text-sm tabular-nums text-[var(--ess-ink)] font-medium md:text-right">{fmtDuration(e.duration_seconds)}</div>
                      <div className="flex justify-end gap-3">
                        {(e.user_id === user.id || user.role === "admin") && (
                          <button onClick={() => setEditing(e)} className="text-gray-500 hover:text-[var(--ess-ink)] p-1" data-testid={`entry-edit-${e.id}`}>
                            <Pencil size={16} />
                          </button>
                        )}
                        {(e.user_id === user.id || user.role === "admin") && (
                          <button onClick={() => onDelete(e.id)} className="text-gray-500 hover:text-red-600 p-1" data-testid={`entry-delete-${e.id}`}>
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      )}

      {pendingTimer && (
        <PendingTimerModal
          pending={pendingTimer}
          projects={projects}
          onCancel={async () => {
            if (window.confirm("Discard this timed entry?")) {
              try {
                // NEW: Delete the incomplete row from DB completely
                await api.delete(`/time-entries/${pendingTimer.timerId}`);
                toast.success("Discarded timed entry");
              } catch (e) {
                toast.error(formatApiError(e));
              }
              setPendingTimer(null);
            }
          }}
          onSave={updateTimerEntry} // MODIFIED: Points to new function
        />
      )}

      {editing && (
        <EditEntryModal
          entry={editing}
          projects={projects}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await loadAll(); }}
        />
      )}
    </div>
  );
}
// Get local YYYY-MM-DD from any ISO timestamp
function getLocalDateStr(isoString) {
  const d = new Date(isoString);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
function PendingTimerModal({ pending, projects, onCancel, onSave }) {
  const { tags: PREDEFINED_TAGS } = useTags();
  const [desc, setDesc] = useState(pending.description || "");
  const [projectId, setProjectId] = useState(pending.projectId || "");
  const [tags, setTags] = useState(pending.tags || []);
  const [saving, setSaving] = useState(false);
  const dur = Math.max(0, Math.floor((new Date(pending.endISO) - new Date(pending.startISO)) / 1000));

  const submit = async (e) => {
    e.preventDefault();
    if (!desc.trim()) return toast.error("Description required");
    if (!projectId) return toast.error("Project required");
    if (tags.length === 0) return toast.error("At least one tag required");
    setSaving(true);
    // Pass startISO and endISO so updateTimerEntry can send them to the backend
    await onSave({ 
      timerId: pending.timerId, 
      startISO: pending.startISO, 
      endISO: pending.endISO, 
      description: desc.trim(), 
      projectId, 
      tags 
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="pending-timer-modal">
      <div className="bg-white border border-gray-200 w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="ess-section-label">Timer stopped</div>
            <h3 className="text-lg font-semibold">Save your timed entry</h3>
            <p className="text-xs text-gray-500 mt-1">
              Duration: <span className="font-medium tabular-nums text-[var(--ess-ink)]">{fmtDuration(dur)}</span> &middot; {new Date(pending.startISO).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} – {new Date(pending.endISO).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
            </p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="ess-section-label block mb-1">Description</label>
            <input value={desc} onChange={(e) => setDesc(e.target.value)} required autoFocus className="w-full border border-gray-200 px-3 py-2 text-sm" data-testid="pending-desc" />
          </div>
          <div>
            <label className="ess-section-label block mb-1">Project</label>
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)} required className="w-full border border-gray-200 px-3 py-2 text-sm" data-testid="pending-project">
              <option value="">Select project…</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name} — {p.client}</option>)}
            </select>
          </div>
          <div>
            <label className="ess-section-label block mb-1">Tags</label>
            <div className="flex flex-wrap gap-2">
              {PREDEFINED_TAGS.map((t) => (
                <label key={t} className={`text-xs uppercase tracking-wider px-2 py-1 cursor-pointer border ${tags.includes(t) ? "bg-[var(--ess-ink)] text-white border-[var(--ess-ink)]" : "border-gray-200"}`}>
                  <input type="checkbox" className="hidden" checked={tags.includes(t)} onChange={(e) => setTags((s) => e.target.checked ? [...s, t] : s.filter((x) => x !== t))} />
                  {t}
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-between items-center pt-3 border-t border-gray-100">
            <button type="button" onClick={onCancel} className="text-xs uppercase tracking-wider underline text-red-600">Discard</button>
            <button type="submit" disabled={saving} className="bg-[var(--ess-ink)] text-white px-4 py-2 text-sm font-semibold uppercase tracking-wider" data-testid="pending-save">
              {saving ? "Saving..." : "Save entry"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CalendarView({ entries, month, setMonth, selectedDay, setSelectedDay, user, onEdit, onDelete }) {
  const year = month.getFullYear();
  const m = month.getMonth();
  const firstDay = new Date(year, m, 1);
  const lastDay = new Date(year, m + 1, 0);
  const startWeekday = firstDay.getDay(); // 0=Sun
  const daysInMonth = lastDay.getDate();
  const monthLabel = month.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  // aggregate entries by date
  const byDay = useMemo(() => {
    const m = {};
    for (const e of entries) {
        const localDate = getLocalDateStr(e.start_time);
        if (!m[localDate]) m[localDate] = { total: 0, items: [] };
        m[localDate].total += e.duration_seconds;
        m[localDate].items.push(e);
    }
    return m;
  }, [entries]);

  const prev = () => setMonth(new Date(year, m - 1, 1));
  const next = () => setMonth(new Date(year, m + 1, 1));
  const localDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const today = localDateStr(new Date());

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = new Date(year, m, d, 12).toISOString().slice(0, 10);
    cells.push({ d, iso });
  }
  while (cells.length % 7 !== 0) cells.push(null);

  const monthTotal = Object.entries(byDay).reduce((s, [k, v]) =>
    k.startsWith(`${year}-${String(m + 1).padStart(2, "0")}`) ? s + v.total : s, 0);

  const selectedEntries = selectedDay ? (byDay[selectedDay]?.items || []) : [];

  return (
    <div className="px-4 sm:px-8 pb-8" data-testid="calendar-view">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <button onClick={prev} className="border border-gray-200 p-2 hover:bg-gray-50" data-testid="cal-prev"><ChevronLeft size={16} /></button>
          <div className="text-xl font-semibold tracking-tight text-[var(--ess-ink)]" data-testid="cal-month">{monthLabel}</div>
          <button onClick={next} className="border border-gray-200 p-2 hover:bg-gray-50" data-testid="cal-next"><ChevronRight size={16} /></button>
          <button
            onClick={() => { const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)); setSelectedDay(today); }}
            className="text-xs uppercase tracking-wider underline text-gray-500"
            data-testid="cal-today"
          >
            Today
          </button>
        </div>
        <div className="text-sm text-gray-500">
          Month total: <span className="text-[var(--ess-ink)] font-medium tabular-nums">{fmtDuration(monthTotal)}</span>
        </div>
      </div>

      <div className="grid grid-cols-7 border border-gray-200">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((wd) => (
          <div key={wd} className="px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-500 bg-[var(--ess-surface)] border-b border-gray-200">{wd}</div>
        ))}
        {cells.map((c, i) => {
          if (!c) return <div key={i} className="min-h-[96px] bg-[var(--ess-surface)]/50 border-t border-l border-gray-100" />;
          const info = byDay[c.iso];
          const isToday = c.iso === today;
          const isSelected = c.iso === selectedDay;
          return (
            <button
              key={c.iso}
              onClick={() => setSelectedDay(c.iso === selectedDay ? null : c.iso)}
              className={`text-left min-h-[96px] border-t border-l border-gray-100 px-3 py-2 transition-colors ${isSelected ? "bg-[var(--ess-ink)] text-white" : info ? "bg-white hover:bg-gray-50" : "bg-white hover:bg-gray-50"}`}
              data-testid={`cal-day-${c.iso}`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-sm tabular-nums ${isToday && !isSelected ? "text-[var(--ess-ink)] font-bold underline underline-offset-4" : isSelected ? "text-white font-semibold" : "text-gray-700"}`}>{c.d}</span>
                {info && <span className={`text-[10px] ${isSelected ? "text-white/70" : "text-gray-400"}`}>{info.items.length}</span>}
              </div>
              {info && (
                <div className={`mt-2 text-[11px] tabular-nums ${isSelected ? "text-white" : "text-[var(--ess-ink)] font-medium"}`}>
                  {fmtDuration(info.total)}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {selectedDay && (
        <div className="mt-6 border border-gray-200" data-testid="cal-selected-entries">
          <div className="px-4 py-3 border-b border-gray-200 bg-[var(--ess-surface)] flex items-center justify-between">
            <div className="ess-section-label">
              {new Date(selectedDay + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
            </div>
            <button onClick={() => setSelectedDay(null)} className="text-gray-400 hover:text-gray-700"><X size={14} /></button>
          </div>
          {selectedEntries.length === 0 && (
            <div className="px-4 py-6 text-sm text-gray-500 text-center">No entries on this day.</div>
          )}
          {selectedEntries.map((e) => (
            <div key={e.id} className="flex flex-col md:grid md:grid-cols-12 md:items-center px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 gap-1 md:gap-0">
              <div className="md:col-span-4 text-sm text-[var(--ess-ink)] font-medium md:truncate">{e.description}</div>
              <div className="md:col-span-2 text-xs text-gray-500 truncate">{e.project_name}</div>
              <div className="md:col-span-3 flex flex-wrap gap-1">
                {e.tags.map((t) => <span key={t} className="text-[10px] uppercase tracking-wider bg-gray-100 px-1.5 py-0.5">{t}</span>)}
              </div>
              <div className="md:col-span-1 text-xs text-gray-500 tabular-nums">{fmtTime(e.start_time)} – {fmtTime(e.end_time)}</div>
              <div className="flex items-center justify-between md:justify-end gap-3 md:col-span-2">
                <div className="text-sm tabular-nums text-[var(--ess-ink)] font-medium md:text-right">{fmtDuration(e.duration_seconds)}</div>
                <div className="flex gap-3">
                  {(e.user_id === user.id || user.role === "admin") && (
                    <>
                      <button onClick={() => onEdit(e)} className="text-gray-500 hover:text-[var(--ess-ink)] p-1"><Pencil size={16} /></button>
                      <button onClick={() => onDelete(e.id)} className="text-gray-500 hover:text-red-600 p-1"><Trash2 size={16} /></button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EditEntryModal({ entry, projects, onClose, onSaved }) {
  const { tags: PREDEFINED_TAGS } = useTags();
  const [desc, setDesc] = useState(entry.description);
  const [projectId, setProjectId] = useState(entry.project_id);
  const [tags, setTags] = useState(entry.tags || []);
  const [date, setDate] = useState(entry.date);
  const [start, setStart] = useState(() => {
    const d = new Date(entry.start_time);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  });
  const [end, setEnd] = useState(() => {
    const d = new Date(entry.end_time);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (tags.length === 0) return toast.error("At least one tag required");
    setSaving(true);
    try {
      await api.put(`/time-entries/${entry.id}`, {
        description: desc.trim(),
        project_id: projectId,
        tags,
        start_time: combineDateTime(date, start),
        end_time: combineDateTime(date, end),
      });
      toast.success("Updated");
      onSaved();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="edit-entry-modal">
      <div className="bg-white border border-gray-200 w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Edit Entry</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="ess-section-label block mb-1">Description</label>
            <input value={desc} onChange={(e) => setDesc(e.target.value)} required className="w-full border border-gray-200 px-3 py-2 text-sm" data-testid="edit-description" />
          </div>
          <div>
            <label className="ess-section-label block mb-1">Project</label>
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)} required className="w-full border border-gray-200 px-3 py-2 text-sm" data-testid="edit-project">
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name} — {p.client}</option>)}
            </select>
          </div>
          <div>
            <label className="ess-section-label block mb-1">Tags</label>
            <div className="flex flex-wrap gap-2">
              {PREDEFINED_TAGS.map((t) => (
                <label key={t} className={`text-xs uppercase tracking-wider px-2 py-1 cursor-pointer border ${tags.includes(t) ? "bg-[var(--ess-ink)] text-white border-[var(--ess-ink)]" : "border-gray-200"}`}>
                  <input type="checkbox" className="hidden" checked={tags.includes(t)} onChange={(e) => setTags((s) => e.target.checked ? [...s, t] : s.filter((x) => x !== t))} />
                  {t}
                </label>
              ))}
            </div>
          </div>
                              <div className="grid grid-cols-3 gap-2">
            <div><label className="ess-section-label block mb-1">Date</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full border border-gray-200 px-2 py-2 text-sm" data-testid="edit-date" /></div>
            <div>
              <label className="ess-section-label block mb-1">Start</label>
              <TimePicker24h
                value={start}
                onChange={setStart}
                testid="edit-start"
              />
            </div>
            <div>
              <label className="ess-section-label block mb-1">End</label>
              <TimePicker24h
                value={end}
                onChange={setEnd}
                testid="edit-end"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <button type="button" onClick={onClose} className="border border-gray-200 px-4 py-2 text-sm">Cancel</button>
            <button type="submit" disabled={saving} className="bg-[var(--ess-ink)] text-white px-4 py-2 text-sm font-semibold uppercase tracking-wider" data-testid="edit-save">{saving ? "Saving..." : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
